import { useState } from 'react';

import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

import { POLICY_IDS, POLICY_NAMES } from '@/features/decisions/config/personaContext';

type ScenarioKey = keyof typeof POLICY_IDS;
type Status = 'Active' | 'Fixed' | 'Inactive' | 'Derived' | 'Optimized';

type Cell = { value: string; status: Status | null; was?: string };

type SubItem = {
  label: string;
  values: Record<ScenarioKey, Cell>;
};

type ItemData = {
  name: string;
  values: Record<ScenarioKey, Cell>;
  subItems?: SubItem[];
};

type Section = {
  title: string;
  items: ItemData[];
};

const cashCell = (value: string, scenario: 'active' | 'inactive'): Record<ScenarioKey, Cell> => ({
  incumbent: { value, status: 'Inactive' },
  keystoneForecast: { value, status: 'Inactive' },
  keystoneOptimized: { value, status: scenario === 'active' ? 'Active' : 'Inactive' },
});

const POLICY_SECTIONS: Section[] = [
  {
    title: 'Decisions',
    items: [
      {
        name: 'Production Cycle',
        values: {
          incumbent: { value: 'Monthly · 4 wk', status: 'Fixed' },
          keystoneForecast: { value: 'Monthly · 4 wk', status: 'Fixed' },
          keystoneOptimized: { value: 'Biweekly', status: 'Optimized', was: 'Monthly - 4 wk' },
        },
      },
      {
        name: 'Production Quantity',
        values: {
          incumbent: { value: 'Demand Driven Q (MAD SS/CC Policy)', status: 'Derived' },
          keystoneForecast: { value: 'Optimized - Forecast + TRNQ', status: 'Optimized' },
          keystoneOptimized: { value: 'Optimized - Forecast + TRNQ', status: 'Optimized' },
        },
      },
    ],
  },
  {
    title: 'Intermediate Constraints',
    items: [
      {
        name: 'Production Lead Time',
        values: {
          incumbent: { value: '2', status: 'Active' },
          keystoneForecast: { value: '2', status: 'Active' },
          keystoneOptimized: { value: '1', status: 'Optimized', was: '2' },
        },
      },
      {
        name: 'Target Service Level',
        values: {
          incumbent: { value: '90%', status: 'Active' },
          keystoneForecast: { value: '90%', status: 'Active' },
          keystoneOptimized: { value: '95%', status: 'Optimized', was: '90%' },
        },
      },
      {
        name: 'Service Level Type',
        values: {
          incumbent: { value: 'Alpha Cycle', status: 'Active' },
          keystoneForecast: { value: 'Alpha Cycle', status: 'Active' },
          keystoneOptimized: { value: 'Beta', status: 'Optimized' },
        },
      },
      {
        name: 'Min Batch Size',
        values: {
          incumbent: { value: '1', status: 'Active' },
          keystoneForecast: { value: '1', status: 'Active' },
          keystoneOptimized: { value: '1', status: 'Active' },
        },
      },
    ],
  },
  {
    title: 'Cash Inputs',
    items: [
      { name: 'Material Cost', values: cashCell('$28.2/unit', 'active') },
      { name: 'Variable Conversion', values: cashCell('$10.5/unit', 'active') },
      { name: 'Fixed Conversion', values: cashCell('$224.5/run', 'active') },
      { name: 'Cash Collected', values: cashCell('$100.20/unit', 'active') },
      {
        name: 'In-Stock Value',
        values: cashCell('—', 'active'),
        subItems: [
          {
            label: 'Short-Term Spillover',
            values: cashCell('10% (≤2 wk) / 20% (4 wk) / 100% (2 mo)', 'active'),
          },
          {
            label: 'Long-Term In-Stock',
            values: cashCell('n/a', 'inactive'),
          },
        ],
      },
      { name: 'Salvage', values: cashCell('n/a', 'inactive') },
      { name: 'Disposal', values: cashCell('n/a', 'inactive') },
    ],
  },
];

function StatusBadge({ status }: { status: Status | null }) {
  if (!status) return <span className="text-gray-300">—</span>;
  if (status === 'Active') {
    return (
      <span className="inline-flex items-center rounded-md bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-600/20">
        Active
      </span>
    );
  }
  if (status === 'Optimized') {
    return (
      <span className="inline-flex items-center rounded-md bg-gray-900 px-2.5 py-1 text-xs font-semibold text-white">
        Optimized
      </span>
    );
  }
  if (status === 'Derived') {
    return (
      <span className="inline-flex items-center rounded-md bg-blue-50 px-2.5 py-1 text-xs font-semibold text-[#274483] ring-1 ring-inset ring-[#274483]/20">
        Derived
      </span>
    );
  }
  if (status === 'Inactive') {
    return (
      <span className="inline-flex items-center rounded-md bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-400 ring-1 ring-inset ring-gray-300/50">
        Inactive
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500 ring-1 ring-inset ring-gray-500/20">
      Fixed input
    </span>
  );
}

function ItemRow({ item, scenario }: { item: ItemData; scenario: ScenarioKey }) {
  const cell = item.values[scenario];

  if (item.subItems) {
    return (
      <Disclosure defaultOpen>
        <DisclosureButton className="group grid w-full grid-cols-3 items-center px-6 py-2.5 text-sm text-left">
          <span className="flex items-center gap-1 text-gray-600">
            <ChevronDownIcon className="h-3 w-3 text-gray-400 transition-transform group-data-[open]:rotate-180" />
            {item.name}
            <span className="ml-1 text-[10px] font-medium text-gray-400 uppercase tracking-wide">multipliers</span>
          </span>
          <span className={`text-right ${cell.value === '—' ? 'text-gray-300' : 'text-gray-700'}`}>{cell.value}</span>
          <div className="flex justify-end">
            <StatusBadge status={cell.status} />
          </div>
        </DisclosureButton>
        <DisclosurePanel>
          {item.subItems.map((sub) => {
            const subCell = sub.values[scenario];
            return (
              <div
                key={sub.label}
                className="grid grid-cols-3 items-center border-t border-gray-50 bg-gray-50/50 px-6 py-2 text-sm"
              >
                <span className="pl-5 text-gray-500 text-xs">{sub.label}</span>
                <span
                  className={`text-right text-xs ${subCell.value === 'n/a' ? 'text-gray-400 italic' : 'text-gray-600'}`}
                >
                  {subCell.value}
                </span>
                <div className="flex justify-end">
                  <StatusBadge status={subCell.status} />
                </div>
              </div>
            );
          })}
        </DisclosurePanel>
      </Disclosure>
    );
  }

  return (
    <div className="grid grid-cols-3 items-center px-6 py-2.5 text-sm">
      <span className="text-gray-600">{item.name}</span>
      <div className="flex flex-col items-end">
        <span className={cell.value === '—' || cell.value === 'n/a' ? 'text-gray-300' : 'text-gray-700'}>
          {cell.value}
        </span>
        {cell.was && <span className="text-xs text-amber-500">was {cell.was}</span>}
      </div>
      <div className="flex justify-end">
        <StatusBadge status={cell.status} />
      </div>
    </div>
  );
}

export function PoliciesConstraintsCashInputs() {
  const [scenario, setScenario] = useState<ScenarioKey>('incumbent');

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
      {/* Right-aligned scenario selector — only Value & Status columns change */}
      <div className="flex items-center justify-end gap-1 px-4 py-2">
        <span className="mr-2 text-xs text-gray-400">Viewing:</span>
        {(Object.keys(POLICY_IDS) as ScenarioKey[]).map((key) => (
          <button
            key={key}
            type="button"
            onClick={() => setScenario(key)}
            className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${
              scenario === key ? 'bg-[#274483] text-white' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
            }`}
          >
            {POLICY_NAMES[POLICY_IDS[key]]}
          </button>
        ))}
      </div>

      {/* Column headers */}
      <div className="grid grid-cols-3 px-6 py-2 bg-gray-50 text-xs font-semibold text-gray-400 uppercase tracking-wide">
        <span>Name</span>
        <span className="text-right">Value</span>
        <span className="text-right">Status</span>
      </div>

      {POLICY_SECTIONS.map((section) => (
        <Disclosure key={section.title} defaultOpen>
          <DisclosureButton className="group flex w-full items-center justify-between px-4 py-3 text-left">
            <span className="text-sm font-semibold text-gray-700">{section.title}</span>
            <ChevronDownIcon className="h-4 w-4 text-gray-400 transition-transform group-data-[open]:rotate-180" />
          </DisclosureButton>
          <DisclosurePanel className="border-t border-gray-100">
            {section.items.map((item) => (
              <ItemRow key={item.name} item={item} scenario={scenario} />
            ))}
          </DisclosurePanel>
        </Disclosure>
      ))}
    </div>
  );
}
